import React, { useState, useCallback, useRef } from "react";
import { 
  Card, Box, Stack, Avatar, Typography, IconButton, 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, 
  ListItemAvatar, ListItemText, Tooltip, CircularProgress, Menu, MenuItem, ListItemIcon
} from "@mui/material";
import { 
  MoreHoriz, Public, Close, People, Lock, PersonRemove, PersonAdd, Settings, Edit, Delete 
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ExpandableText from "./ExpandableText";
import PostInteractionBar from "./PostInteractionBar"; 
import { REACCIONES_MAP } from "./ReaccionesConstants";
import PostCommentsSection from "./PostCommentsSection";

const PRIVACY_CONFIG = {
  public: { icon: <Public sx={{ fontSize: 14 }} />, label: "Público" },
  friends: { icon: <People sx={{ fontSize: 14 }} />, label: "Amigos" },
  friends_except: { icon: <PersonRemove sx={{ fontSize: 14 }} />, label: "Amigos excepto..." },
  specific_friends: { icon: <PersonAdd sx={{ fontSize: 14 }} />, label: "Amigos concretos" },
  private: { icon: <Lock sx={{ fontSize: 14 }} />, label: "Solo yo" },
  custom: { icon: <Settings sx={{ fontSize: 14 }} />, label: "Personalizado" },
};

const PostCard = ({ post, userId, userAvatar, currentUserName, queryUsers, API_SOCIAL, onEdit, onDelete, onLikeUpdate, isHighlighted, activeProfile }) => {
  const navigate = useNavigate();
  const [dominantColor, setDominantColor] = useState("#f0f2f5");
  const colorExtracted = useRef(false);

  const extractColor = useCallback((imgUrl) => {
    if (colorExtracted.current) return;
    colorExtracted.current = true;
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = imgUrl;
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        const ctx = c.getContext("2d", { willReadFrequently: true });
        const w = img.naturalWidth, h = img.naturalHeight;
        c.width = w; c.height = h;
        ctx.drawImage(img, 0, 0);
        const s = 5;
        const top = ctx.getImageData(0, 0, w, s).data;
        const bottom = ctx.getImageData(0, h - s, w, s).data;
        const d = new Uint8ClampedArray(top.length + bottom.length);
        d.set(top); d.set(bottom, top.length);
        let r = 0, g = 0, b = 0;
        for (let i = 0; i < d.length; i += 4) {
          r += d[i]; g += d[i + 1]; b += d[i + 2];
        }
        const n = d.length / 4;
        setDominantColor(`rgb(${Math.round(r/n)},${Math.round(g/n)},${Math.round(b/n)})`);
      } catch { setDominantColor("#f0f2f5"); }
    };
  }, []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [mainCommentText, setMainCommentText] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  
  const [openReactionsModal, setOpenReactionsModal] = useState(false);
  const [reactionsDetail, setReactionsDetail] = useState([]);
  const [loadingReactions, setLoadingReactions] = useState(false);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  const currentPrivacy = PRIVACY_CONFIG[post.privacy_type] || PRIVACY_CONFIG.public;

  // Navegación al perfil
  const handleGoToProfile = useCallback((id, nombre, apellido) => {
    if (!id) return;
    const cleanNombre = String(nombre || "usuario").toLowerCase().trim().replace(/\s+/g, '');
    const cleanApellido = String(apellido || "").toLowerCase().trim().replace(/\s+/g, '');
    const slug = cleanApellido ? `${cleanNombre}.${cleanApellido}` : cleanNombre;

    navigate(`/${slug}?tab=Publicaciones`, { 
      state: { targetId: id } 
    });
  }, [navigate]);

  // Cargar reacciones detalladas
  const handleOpenReactionsModal = async () => {
    setOpenReactionsModal(true);
    setLoadingReactions(true);
    try {
      const url = `${API_SOCIAL}?action=postReactions&postId=${post.id}`;
      const res = await axios.get(url);
      setReactionsDetail(res.data && Array.isArray(res.data) ? res.data : (post.reactions_detail || []));
    } catch (e) {
      setReactionsDetail(post.reactions_detail || []);
    } finally {
      setLoadingReactions(false);
    }
  };

  // Cargar comentarios
  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_SOCIAL}?action=comments&postId=${post.id}&userId=${userId}`);
      setComments(Array.isArray(res.data) ? res.data : []);
    } catch (e) { }
  }, [API_SOCIAL, post.id, userId]);

  const toggleComments = async () => {
    if (!showComments) {
      setLoadingComments(true);
      await fetchComments();
      setLoadingComments(false);
    }
    setShowComments(!showComments);
  };

  // Enviar comentario
  const handleSendComment = async (text, parentId = null) => {
    if (!text || !text.trim()) return false;
    try {
      const res = await axios.post(`${API_SOCIAL}`, {
        action: "comment", postId: post.id, userId: userId, comment: text, parentId: parentId,
        author_profile_id: activeProfile?.id || null,
        author_profile_type: activeProfile?.type || null
      });
      if (res.data?.success) {
        if (!parentId) setMainCommentText("");
        await fetchComments();
        if (onLikeUpdate) onLikeUpdate(); 
        return true;
      }
    } catch (e) { }
    return false;
  };

  // Reacción al post
  const handlePostReaction = async (type) => {
    try {
      const res = await axios.post(`${API_SOCIAL}`, { 
        action: "toggleLike", postId: post.id, userId, reactionType: type,
        author_profile_id: activeProfile?.id || null,
        author_profile_type: activeProfile?.type || null
      });
      if (res.data.success) onLikeUpdate(); 
    } catch (e) { }
  };

  // Reacción al comentario
  const handleCommentReaction = async (commentId, type) => {
    try {
      const res = await axios.post(`${API_SOCIAL}`, {
        action: "toggleCommentLike", commentId, userId, reactionType: type,
        author_profile_id: activeProfile?.id || null,
        author_profile_type: activeProfile?.type || null
      });
      if (res.data.success) await fetchComments();
    } catch (e) { }
  };

  // Borrar comentario
  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;
    try {
      const res = await axios.post(`${API_SOCIAL}`, {
        action: "deleteComment", commentId: commentToDelete, userId: userId 
      });
      if (res.data.success) {
        await fetchComments();
        onLikeUpdate();
      }
    } catch (e) { }
    finally { setOpenDeleteDialog(false); setCommentToDelete(null); }
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: isHighlighted ? "0 0 0 2px #0a66c2, 0 4px 16px rgba(10,102,194,0.15)" : "0 1px 2px rgba(0,0,0,0.1)", mb: 2, border: "none", overflow: 'visible', maxWidth: { xs: '100%', md: '600px' }, mx: { md: 'auto' }, animation: isHighlighted ? 'pulseBorder 2s ease-in-out 3' : 'none', '@keyframes pulseBorder': { '0%': { boxShadow: '0 0 0 2px #0a66c2, 0 4px 16px rgba(10,102,194,0.15)' }, '50%': { boxShadow: '0 0 0 4px #0a66c2, 0 4px 24px rgba(10,102,194,0.25)' }, '100%': { boxShadow: '0 0 0 2px #0a66c2, 0 4px 16px rgba(10,102,194,0.15)' } } }}>
      
      {/* 1. CABECERA DEL POST */}
      <Box sx={{ p: 2, pb: 1, display: "flex", justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1.5}>
            <Avatar 
              src={post.user_avatar} 
              onClick={() => handleGoToProfile(post.user_id, post.nombre_real, post.apellido_real)}
              sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 }, width: 40, height: 40 }}
            />
            <Box>
                <Typography 
                  onClick={() => handleGoToProfile(post.user_id, post.nombre_real, post.apellido_real)}
                  sx={{ fontWeight: 600, fontSize: "0.93rem", cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, lineHeight: 1.2 }}
                >
                  {post.user_full_name}
                </Typography>

                {/* Cargo del Usuario */}
                {post.nombre_cargo && (
                  <Typography 
                    variant="caption" 
                    sx={{ display: 'block', color: 'primary.main', fontWeight: 600, fontSize: '0.72rem', mt: 0.1, mb: 0.2 }}
                  >
                    {post.nombre_cargo}
                  </Typography>
                )}

                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "#65676b" }}>
                    <Typography variant="caption">{new Date(post.created_at).toLocaleString()}</Typography>
                    <Tooltip title={currentPrivacy.label}>
                     <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>{currentPrivacy.icon}</Box>
                    </Tooltip>
                </Stack>
            </Box>
          </Stack>
          
          {/* Menú de opciones (Solo para el dueño) */}
          {Number(post.user_id) === Number(userId) && (
            <>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                <MoreHoriz />
              </IconButton>
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ zIndex: 9999 }}
                PaperProps={{ sx: { boxShadow: '0 4px 20px rgba(0,0,0,0.2)', borderRadius: 2, minWidth: 150 } }}
              >
                <MenuItem onClick={() => { 
                  onEdit(post); 
                  setAnchorEl(null); 
                }}>
                  <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
                  <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { 
                  onDelete(post); 
                  setAnchorEl(null); 
                }} sx={{ color: 'error.main' }}>
                  <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>Eliminar</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
      </Box>

      {/* 2. CONTENIDO DE TEXTO */}
      <Box sx={{ px: 2, pb: 1 }}>
        <ExpandableText text={post.content} />
      </Box>

      
      {/* 3. IMAGEN ADAPTABLE */}
      {post.image_url && (
        <Box sx={{ width: "100%", bgcolor: dominantColor, display: "flex", justifyContent: "center", borderTop: '1px solid #f0f2f5', borderBottom: '1px solid #f0f2f5' }}>
          <Box 
            component="img" 
            src={post.image_url}
            loading="lazy"
            onLoad={(e) => {
              extractColor(post.image_url);
              const img = e.target;
              const { naturalWidth, naturalHeight } = img;
              const aspectRatio = naturalWidth / naturalHeight;
               
               const availableFormats = {
                 "9:16 (Vertical, Reels/Stories)": 9/16,
                 "3:4 (Vertical, Perfil Optimizado)": 3/4,
                 "4:5 (Vertical, Feed Recomendado)": 4/5,
                 "1:1 (Cuadrado)": 1/1,
                 "1.91:1 (Horizontal, Stream)": 1.91/1,
                 "16:9 (Horizontal, Videos)": 16/9
               };

               let detectedFormat = "Desconocido";
               let targetMaxHeight = "auto";

               if (aspectRatio <= 0.6) {
                 detectedFormat = "Vertical (9:16 o similar)";
                 targetMaxHeight = "1920px"; 
               } else if (aspectRatio <= 0.78) {
                 detectedFormat = "Vertical (3:4)";
                 targetMaxHeight = "1440px";
               } else if (aspectRatio <= 0.85) {
                 detectedFormat = "Vertical (4:5)";
                 targetMaxHeight = "1350px";
               } else if (aspectRatio <= 1.05) {
                 detectedFormat = "Cuadrado (1:1)";
                 targetMaxHeight = "1080px";
               } else if (aspectRatio >= 1.7) {
                 detectedFormat = "Horizontal (1.91:1 o 16:9)";
                 targetMaxHeight = "566px";
               }

              // The image element itself (`<Box component="img">`) should dictate its height
              // based on its `width: "100%"` and `height: "auto"` while respecting `objectFit: "contain"`.
              // The parent `Box` container (line 274) has `width: "100%"` and `height: "auto"` by default,
              // allowing it to expand vertically to fit the image without extra borders.
              // The `maxHeight` values in the `sx` prop (e.g., `md: "620px"`) will still act as upper limits.
            }}
            sx={{
              width: "100%",
              maxWidth: "600px",
              height: "auto",
              maxHeight: { xs: "70vh", sm: "450px", md: "550px" },
              display: "block",
              objectFit: "contain"
            }} 
          />
        </Box>
      )}

      {/* 4. BARRA DE INTERACCIÓN (Likes, Reacciones, Contador) */}
      <PostInteractionBar 
        post={post} 
        onReactionSelect={handlePostReaction} 
        onCommentClick={toggleComments} 
        onOpenReactionsModal={handleOpenReactionsModal}
        currentUserId={userId}
        currentUserName={currentUserName}
        queryUsers={queryUsers}
        activeProfile={activeProfile}
      />

      {/* 5. SECCIÓN DE COMENTARIOS */}
      {showComments && (
        <PostCommentsSection 
          comments={comments}
          loading={loadingComments}
          userAvatar={userAvatar}
          currentUserId={userId}
          postOwnerId={post.user_id}
          queryUsers={queryUsers} 
          mainCommentText={mainCommentText}
          setMainCommentText={setMainCommentText}
          onCommentSubmit={handleSendComment}
          onCommentReaction={handleCommentReaction}
          onCommentDelete={(id) => { setCommentToDelete(id); setOpenDeleteDialog(true); }}
        />
      )}

      {/* MODAL DE LISTADO DE REACCIONES */}
      <Dialog 
        open={openReactionsModal} 
        onClose={() => setOpenReactionsModal(false)} 
        fullWidth 
        maxWidth="xs" 
        sx={{ zIndex: 99999 }}
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '70vh' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem", borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Reacciones 
          <IconButton onClick={() => setOpenReactionsModal(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loadingReactions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={30} /></Box>
          ) : (
            <List sx={{ py: 0 }}>
              {reactionsDetail.map((reac, idx) => (
                <ListItem 
                  key={idx} 
                  onClick={() => handleGoToProfile(reac.id_usuario || reac.userId, reac.nombre, reac.apellido)} 
                  sx={{ '&:hover': { bgcolor: '#f0f2f5', cursor: 'pointer' }, px: 2 }}
                >
                  <ListItemAvatar sx={{ position: 'relative' }}>
                    <Avatar src={reac.avatar} />
                  </ListItemAvatar>
                  <ListItemText 
                    primary={`${reac.nombre} ${reac.apellido}`} 
                    primaryTypographyProps={{ fontWeight: 600, fontSize: "0.95rem" }} 
                  />
                  {reac.reaction_type && REACCIONES_MAP[reac.reaction_type] && (
                    <Box component="img" src={REACCIONES_MAP[reac.reaction_type].img} sx={{ width: 20, height: 20, ml: 'auto', flexShrink: 0 }} />
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* DIÁLOGO DE CONFIRMACIÓN PARA BORRAR COMENTARIO */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        sx={{ zIndex: 11000 }}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>¿Borrar comentario?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Esta acción no se puede deshacer. El comentario será eliminado permanentemente.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setOpenDeleteDialog(false)}
            sx={{ color: '#65676b', fontWeight: 600, textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={confirmDeleteComment} 
            variant="contained" 
            color="error"
            sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 2, px: 3 }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default React.memo(PostCard, (prevProps, nextProps) => {
  return (
    prevProps.post === nextProps.post && 
    prevProps.userAvatar === nextProps.userAvatar &&
    prevProps.activeProfile === nextProps.activeProfile
  );
});